import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { getDesignTokens } from '../utils/designTokens';
import NavBar from '../components/NavBar';

export default function ResetPassword() {
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [resendSuccess, setResendSuccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { confirmForgotPassword, forgotPassword, isLoading, error, clearError } = useAuthStore();
  const { isDarkMode } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);

  useEffect(() => {
    // Get email from navigation state or prompt user
    const stateEmail = location.state?.email;
    if (stateEmail) {
      setEmail(stateEmail);
    }
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setResendSuccess(false);

    if (newPassword !== confirmPassword) {
      return;
    }

    if (newPassword.length < 8) {
      return;
    }

    if (!email) {
      return;
    }
    
    try {
      await confirmForgotPassword(email, code, newPassword);
      navigate('/login', { 
        state: { message: 'Password reset successfully! Please sign in with your new password.' }
      });
    } catch (err) {
      // Error is handled by the store
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      // If no email, navigate to forgot password page
      navigate('/forgot-password');
      return;
    }

    clearError();
    setResendSuccess(false);
    
    try {
      await forgotPassword(email);
      setResendSuccess(true);
    } catch (err) {
      // Error is handled by the store
    }
  };

  return (
    <div className={`min-h-screen ${tokens.bg.gradient} flex flex-col relative`}>
      <NavBar className={`${tokens.card.bg} shadow-sm`} containerClassName="px-3 sm:px-4">
        <h1 className={`text-xl font-bold ${tokens.text.primary}`}>Runow</h1>
      </NavBar>

    {/* Main Content */}
    <div className="flex-1 min-h-0 overflow-y-auto scrollable">
      <div className="min-h-full flex items-start sm:items-center justify-center p-4">
        <div className={`max-w-md w-full ${tokens.card.bg} rounded-landing ${tokens.card.shadow} p-6 sm:p-8`}>
        <h1 className={`text-2xl font-bold ${tokens.text.primary} mb-6 text-center`}>
          Runow
        </h1>
        <h2 className={`text-lg font-semibold ${tokens.text.primary} mb-6 text-center`}>
          Reset Password
        </h2>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-sm p-4">
            <p className="text-red-800 text-xs">{error}</p>
          </div>
        )}

        {resendSuccess && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-sm p-4">
            <p className="text-green-800 text-xs">A new reset code has been sent to your email.</p>
          </div>
        )}

        <p className={`text-xs ${tokens.text.muted} mb-6 text-center`}>
          Enter the code sent to your email and your new password.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!email && (
            <div>
              <label htmlFor="email" className={`block text-xs font-medium ${tokens.text.primary} mb-1`}>
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={`w-full px-4 py-3 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent ${tokens.card.bg} ${tokens.text.primary}`}
                placeholder="you@example.com"
              />
            </div>
          )}

          <div>
            <label htmlFor="code" className={`block text-xs font-medium ${tokens.text.primary} mb-1`}>
              Reset Code
            </label>
            <input
              id="code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              required
              maxLength={6}
              className={`w-full px-4 py-3 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent ${tokens.card.bg} ${tokens.text.primary} text-center text-xl tracking-widest`}
              placeholder="000000"
            />
            <p className={`text-[10px] ${tokens.text.muted} mt-1`}>Enter the 6-digit code from your email</p>
          </div>

          <div>
            <label htmlFor="newPassword" className={`block text-xs font-medium ${tokens.text.primary} mb-1`}>
              New Password
            </label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              className={`w-full px-4 py-3 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent ${tokens.card.bg} ${tokens.text.primary}`}
              placeholder="••••••••"
            />
            <p className={`text-[10px] ${tokens.text.muted} mt-1`}>Must be at least 8 characters</p>
          </div>

          <div>
            <label htmlFor="confirmPassword" className={`block text-xs font-medium ${tokens.text.primary} mb-1`}>
              Confirm New Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              className={`w-full px-4 py-3 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent ${tokens.card.bg} ${tokens.text.primary}`}
              placeholder="••••••••"
            />
            {confirmPassword && newPassword !== confirmPassword && (
              <p className="text-[10px] text-red-500 mt-1">Passwords do not match</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading || newPassword !== confirmPassword || newPassword.length < 8}
            className={`w-full ${tokens.button.primary} disabled:opacity-50 disabled:cursor-not-allowed font-semibold py-3 px-4 transition-colors min-h-[48px]`}
          >
            {isLoading ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>

        <div className="mt-6 text-center space-y-2">
          <button
            onClick={handleResendCode}
            disabled={isLoading || !email}
            className={`${tokens.text.accent} hover:opacity-80 font-medium text-sm transition-opacity disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isLoading ? 'Sending...' : 'Resend Code'}
          </button>
          <Link
            to="/login"
            className={`block ${tokens.text.accent} hover:opacity-80 font-medium text-sm transition-opacity`}
          >
            Back to Sign In
          </Link>
        </div>
        </div>
      </div>
      </div>
    </div>
  );
}

